import React, { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { CreditCard, LifeBuoy, ShieldAlert, Sparkles, UserPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  api, fmtDay, fmtInt, fmtMoney, fmtPct, isStaff, lastNDays, pctOf, planOf, timeAgo,
  useConfigFlag, useQuery, useTickets, useUsers,
} from './lib';
import {
  Btn, DateRangeControl, EmptyState, ErrorState, Notice, Page, Panel, RefreshButton, StatCard,
  StatGrid, useDateRange,
} from './ui';
import { ChartTooltip, COLORS, cursorFill, gridProps, xAxisProps, yAxisProps } from './charts';

const PERIODS = [
  { value: '7d', label: '7 days', days: 7 },
  { value: '28d', label: '28 days', days: 28 },
  { value: '90d', label: '90 days', days: 90 },
  { value: 'all', label: 'All time', days: null },
];

/* The main currency first, then the others ("€120.00 + $14.99"). */
function revenueSummary(byCurrency) {
  const entries = Object.entries(byCurrency || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return { main: fmtMoney(0, 'EUR'), rest: null };
  const [[cur, amount], ...others] = entries;
  return {
    main: fmtMoney(amount, cur),
    rest: others.length ? others.map(([c, v]) => fmtMoney(v, c)).join(' + ') : null,
  };
}

const ACTIVITY_ICON = { signup: UserPlus, premium: CreditCard, suggestion: Sparkles };

function ConversionStep({ label, value, detail, last }) {
  return (
    <div className={cn('min-w-0 flex-1 p-4', !last && 'border-b border-border sm:border-b-0 sm:border-r')}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight tabular">{value}</p>
      {detail && <p className="mt-1 text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

export default function OverviewPanel({ onNavigate }) {
  const users = useUsers();
  const tickets = useTickets();
  const beta = useConfigFlag('beta_mode_enabled');
  const revenue = useQuery('revenue:30:db', () => api.get('/admin/revenue?days=30&live=false'));
  const activity = useQuery('activity', () => api.get('/admin/recent-activity?limit=12'));
  const range = useDateRange('28d', PERIODS);
  const conversions = useQuery(
    `conversions:${range.start}:${range.end}`,
    () => api.get(`/admin/conversions?start=${range.start}&end=${range.end}`),
  );

  const stats = useMemo(() => {
    const all = users.data || [];
    const customers = all.filter((u) => !isStaff(u));
    const since = new Date();
    since.setDate(since.getDate() - 28);
    const today = lastNDays(1)[0];
    const yesterday = lastNDays(2)[0];
    const day = (u) => (u.created_at || '').slice(0, 10);
    return {
      customers: customers.length,
      new28: customers.filter((u) => u.created_at && new Date(u.created_at) >= since).length,
      premium: customers.filter((u) => planOf(u) === 'premium').length,
      onboarded: customers.filter((u) => u.onboarding_completed).length,
      today: customers.filter((u) => day(u) === today).length,
      yesterday: customers.filter((u) => day(u) === yesterday).length,
    };
  }, [users.data]);

  const signupSeries = useMemo(() => {
    const byDay = {};
    (users.data || []).filter((u) => !isStaff(u)).forEach((u) => {
      const d = (u.created_at || '').slice(0, 10);
      if (d) byDay[d] = (byDay[d] || 0) + 1;
    });
    return lastNDays(30).map((date) => ({ date, signups: byDay[date] || 0 }));
  }, [users.data]);

  const revenueSeries = useMemo(() => {
    const byDay = Object.fromEntries((revenue.data?.daily_revenue || []).map((d) => [d.date, d]));
    return lastNDays(30).map((date) => ({ date, total: byDay[date]?.total || 0 }));
  }, [revenue.data]);

  const openTickets = (tickets.data || []).filter((t) => t.status === 'open').length;
  const inProgress = (tickets.data || []).filter((t) => t.status === 'in_progress').length;
  const money = revenueSummary(revenue.data?.summary?.by_currency);
  const c = conversions.data;
  const usersLoading = !users.data && users.loading;

  const refreshAll = () => {
    users.refetch();
    tickets.refetch();
    revenue.refetch();
    activity.refetch();
    conversions.refetch();
    beta.refetch();
  };

  return (
    <Page
      title="Overview"
      description="Customers, revenue and support at a glance. Staff accounts (admins and beta testers) are left out of every number."
      actions={<RefreshButton onClick={refreshAll} loading={users.loading || revenue.loading} updatedAt={users.updatedAt} />}
    >
      {(beta.data === true || openTickets > 0) && (
        <div className="space-y-2">
          {beta.data === true && (
            <Notice
              tone="warning"
              icon={ShieldAlert}
              title="Beta mode is on"
              action={<Btn variant="outline" size="sm" className="bg-white" onClick={() => onNavigate('settings')}>Settings</Btn>}
            >
              Only beta testers can use the app. Everyone else sees a maintenance screen.
            </Notice>
          )}
          {openTickets > 0 && (
            <Notice
              tone="info"
              icon={LifeBuoy}
              title={`${openTickets} support ticket${openTickets > 1 ? 's' : ''} waiting for a reply`}
              action={<Btn variant="outline" size="sm" className="bg-white" onClick={() => onNavigate('support')}>Open support</Btn>}
            />
          )}
        </div>
      )}

      {users.error && <ErrorState error={users.error} onRetry={users.refetch} title="Could not load customers" />}

      <StatGrid className="lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Customers"
          value={fmtInt(stats.customers)}
          hint={`+${fmtInt(stats.new28)} in the last 28 days`}
          loading={usersLoading}
          onClick={() => onNavigate('customers')}
        />
        <StatCard
          label="Signups today"
          value={fmtInt(stats.today)}
          hint={`${fmtInt(stats.yesterday)} yesterday`}
          loading={usersLoading}
        />
        <StatCard
          label="Premium"
          value={fmtInt(stats.premium)}
          hint={`${fmtPct(pctOf(stats.premium, stats.customers))} of customers`}
          loading={usersLoading}
        />
        <StatCard
          label="Finished onboarding"
          value={fmtPct(pctOf(stats.onboarded, stats.customers), 0)}
          hint={`${fmtInt(stats.onboarded)} customers`}
          loading={usersLoading}
        />
        <StatCard
          label="Revenue · 30 days"
          value={money.main}
          hint={money.rest ? `+ ${money.rest}` : `${fmtInt(revenue.data?.summary?.transaction_count)} payments`}
          loading={!revenue.data && revenue.loading}
          onClick={() => onNavigate('revenue')}
        />
        <StatCard
          label="Open tickets"
          value={fmtInt(openTickets)}
          hint={`${fmtInt(inProgress)} in progress`}
          tone={openTickets > 0 ? 'danger' : undefined}
          loading={!tickets.data && tickets.loading}
          onClick={() => onNavigate('support')}
        />
      </StatGrid>

      <Panel
        title="Conversion"
        description="Of the people who signed up in this period: how many finished onboarding and how many bought Premium. A recent period understates purchases, because people often buy a few days after signing up."
        actions={<DateRangeControl range={range} />}
        flush
      >
        {conversions.error ? (
          <div className="p-4"><ErrorState error={conversions.error} onRetry={conversions.refetch} /></div>
        ) : (
          <div className={cn('flex flex-col sm:flex-row', conversions.loading && 'opacity-60 transition-opacity')}>
            <ConversionStep label="Signed up" value={fmtInt(c?.new_users)} detail="New accounts in the period" />
            <ConversionStep
              label="Finished onboarding"
              value={fmtPct(c?.signup_to_onboarded_pct)}
              detail={c ? `${fmtInt(c.onboarded)} of ${fmtInt(c.new_users)}` : undefined}
            />
            <ConversionStep
              label="Bought Premium"
              value={fmtPct(c?.signup_to_paid_pct)}
              detail={c ? `${fmtInt(c.paid)} of ${fmtInt(c.new_users)} · ${fmtPct(c.onboarded_to_paid_pct)} of those onboarded` : undefined}
              last
            />
          </div>
        )}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Signups" description="New customer accounts per day, last 30 days" className="lg:col-span-2">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={signupSeries} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="date" tickFormatter={fmtDay} {...xAxisProps} />
                <YAxis {...yAxisProps} />
                <Tooltip cursor={cursorFill} content={<ChartTooltip />} />
                <Bar dataKey="signups" name="Signups" fill={COLORS.ink} radius={[3, 3, 0, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Recent activity" flush className="lg:row-span-2">
          {activity.error ? (
            <div className="p-4"><ErrorState error={activity.error} onRetry={activity.refetch} /></div>
          ) : !activity.data ? (
            <div className="space-y-3 p-4">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-9 animate-pulse rounded-md bg-secondary" />)}
            </div>
          ) : activity.data.length === 0 ? (
            <EmptyState title="No activity yet" />
          ) : (
            <ul className="divide-y divide-border">
              {activity.data.map((a, i) => {
                const Icon = ACTIVITY_ICON[a.type] || Sparkles;
                return (
                  <li key={`${a.type}-${i}`} className="flex items-start gap-3 px-4 py-3">
                    <span className={cn(
                      'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                      a.type === 'premium' ? 'bg-emerald-50 text-emerald-700' : 'bg-secondary text-muted-foreground',
                    )}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground">{a.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{a.description}</p>
                    </div>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{a.time}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel
          title="Revenue"
          description="Recorded payments per day, last 30 days (all currencies added together)"
          className="lg:col-span-2"
          actions={<Btn variant="ghost" size="sm" onClick={() => onNavigate('revenue')}>Details</Btn>}
        >
          {revenue.error ? (
            <ErrorState error={revenue.error} onRetry={revenue.refetch} />
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueSeries} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" tickFormatter={fmtDay} {...xAxisProps} />
                  <YAxis {...yAxisProps} allowDecimals />
                  <Tooltip cursor={cursorFill} content={<ChartTooltip valueFormatter={(v) => (v || 0).toFixed(2)} />} />
                  <Bar dataKey="total" name="Revenue" fill={COLORS.good} radius={[3, 3, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      {users.updatedAt && (
        <p className="text-xs text-muted-foreground">Customer data updated {timeAgo(users.updatedAt)}.</p>
      )}
    </Page>
  );
}
